import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHubStore } from '../stores/hubStore';
import { useUIStore } from '../stores/uiStore';
import { RecruiterHubView } from '../components/hub/RecruiterHubView';
import { FreelancerHubView } from '../components/hub/FreelancerHubView';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useProfileRole } from '../hooks/useProfileRole';
import JobListingType from '../components/jobs/JobListingType';
import JobDetailsPanel from '../components/hub/JobDetailsPanel';
import { ProjectStatus, ProjectStatusOption } from '../types/project';
import { useProjectStatusSelector } from '../hooks/useProjectStatusSelector';
import { DropdownSelect } from '../components/common/DropdownSelect';
import JobPublishedDate from '../components/jobs/JobPublishedDate';
import JobTags from '../components/jobs/JobTags';
import UpsertQuoteModal from '../components/quotes/UpsertQuoteModal';
import { Quote } from '../types/quote';
import { useQuoteStore } from '../stores/quoteStore';

export default function HubPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedQuoteId = searchParams.get('selectedQuoteId');

  const setSelectedQuoteId = useCallback(
    (quoteId: string | null) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (quoteId) {
            next.set('selectedQuoteId', quoteId);
          } else {
            next.delete('selectedQuoteId');
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const { t, i18n } = useTranslation(['hub', 'common', 'jobs', 'quotes']);

  const { isRecruiter } = useProfileRole();

  const navigate = useNavigate();
  const { context, contextLoading, contextError, fetchHubContext } = useHubStore();
  const { setActiveBreadCrumb } = useUIStore();
  const { quotes, fetchQuoteById } = useQuoteStore();

  const [showDesktopDetails, setShowDesktopDetails] = useState(false);
  const [showMobileDetails, setShowMobileDetails] = useState(false);
  const [quoteForEdit, setQuoteForEdit] = useState<Quote | null>(null);
  const [loadedHubQuote, setLoadedHubQuote] = useState<Quote | null>(null);
  const [hubQuoteLoading, setHubQuoteLoading] = useState(false);
  const [hubQuoteError, setHubQuoteError] = useState(false);

  const principalQuoteIdToFetch = context?.principal_quote_id ?? null;

  const hasQuotes =
    (context?.job?.quotes?.length ?? 0) > 0 || !!(context?.principal_quote_id ?? null);

  const shouldShowCommunicationHub = context?.job?.visibility === 'private' || hasQuotes;

  const {
    options: statusOptions,
    selectedOption: selectedStatus,
    setSelectedOption: setSelectedStatus,
  } = useProjectStatusSelector(context?.job?.status as ProjectStatus);

  useEffect(() => {
    if (!jobId) {
      navigate('/dashboard');
      return;
    }
    fetchHubContext(jobId);
  }, [jobId, fetchHubContext, navigate]);

  useEffect(() => {
    if (context?.job?.title) {
      setActiveBreadCrumb(context.job.title);
    }
    return () => {
      setActiveBreadCrumb(null);
    };
  }, [context?.job?.title, setActiveBreadCrumb]);

  useEffect(() => {
    const isEditableStatus = context?.job?.status === 'open' || context?.job?.status === 'paused';

    if (isEditableStatus && !shouldShowCommunicationHub) {
      setShowDesktopDetails(true);
      setShowMobileDetails(true);
    }
  }, [context?.job?.status, shouldShowCommunicationHub]);

  useEffect(() => {
    if (
      !context ||
      (context.view_type !== 'freelancer' && context.view_type !== 'alliance') ||
      !principalQuoteIdToFetch
    ) {
      setLoadedHubQuote(null);
      setHubQuoteLoading(false);
      setHubQuoteError(false);
      return;
    }

    let active = true;
    setLoadedHubQuote(null);
    setHubQuoteLoading(true);
    setHubQuoteError(false);

    void fetchQuoteById(principalQuoteIdToFetch).then(quote => {
      if (!active) return;
      setLoadedHubQuote(quote);
      setHubQuoteError(quote == null);
      setHubQuoteLoading(false);
    });

    return () => {
      active = false;
    };
  }, [context, principalQuoteIdToFetch, fetchQuoteById]);

  const hubQuote: Quote | null | undefined =
    principalQuoteIdToFetch != null
      ? (quotes.find(q => String(q.id) === String(principalQuoteIdToFetch)) ?? loadedHubQuote)
      : null;

  if (!jobId) return null;

  if (contextLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (contextError) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
        <p className="text-red-600">{contextError}</p>
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="text-primary underline"
        >
          {t('backToDashboard', { ns: 'common', defaultValue: 'Back to dashboard' })}
        </button>
      </div>
    );
  }

  if (!context) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <p className="text-gray-500">{t('loading')}</p>
      </div>
    );
  }

  const isRecruiterView =
    context.view_type === 'recruiter_multi' || context.view_type === 'recruiter_project';
  const isFreelancerView = context.view_type === 'freelancer';
  const isAllianceView = context.view_type === 'alliance';

  return (
    <div className="mb-8 flex flex-col md:min-h-[calc(100vh-4rem)] md:px-5 lg:px-32">
      <h1 className="mb-2 w-full text-6xl font-bold text-secondary">{context.job.title}</h1>

      <div
        className={`mb-4 flex items-center gap-8 rounded-lg py-4 md:relative ${showDesktopDetails ? 'bg-white rounded-tl-3xl rounded-tr-3xl mb-16 md:pb-8 md:pb-2 md:px-4' : ''}`}
      >
        <button
          onClick={() => setShowMobileDetails(!showMobileDetails)}
          className={`md:hidden flex flex-row items-center gap-1 rounded-full border border-primary px-3 py-1 text-xs font-semibold cursor-pointer text-primary ${showMobileDetails ? 'bg-primary text-white' : ''}`}
        >
          <h3 className="text-sm font-medium">{t('project_details', { ns: 'hub' })}</h3>
          {showMobileDetails ? (
            <ChevronUp className="h-4 w-4 cursor-pointer" />
          ) : (
            <ChevronDown className="h-4 w-4 cursor-pointer" />
          )}
        </button>
        <button
          onClick={() => setShowDesktopDetails(!showDesktopDetails)}
          className={`hidden md:flex flex-row items-center gap-1 rounded-full border border-primary px-3 py-1 text-xs font-semibold cursor-pointer text-primary ${showDesktopDetails ? 'bg-primary text-white' : ''}`}
        >
          <h3 className="text-sm font-medium">{t('project_details', { ns: 'hub' })}</h3>
          {showDesktopDetails ? (
            <ChevronUp className="h-4 w-4 cursor-pointer" />
          ) : (
            <ChevronDown className="h-4 w-4 cursor-pointer" />
          )}
        </button>

        {showDesktopDetails && (
          <div className="animate-fade-in absolute right-0 top-16 z-40 flex w-full flex-col gap-2 rounded-bl-3xl rounded-br-3xl bg-white p-4 shadow-2xl">
            <JobDetailsPanel job={context.job} />
          </div>
        )}

        <JobPublishedDate job={context.job} t={t} language={i18n.language} className="text-xs" />

        {isRecruiter ? (
          <>
            <JobTags job={context.job} t={t} variant="hub" />
            {context.accepted_quote && context.job.visibility === 'private' && (
              <div>
                <span className="w-max rounded-full bg-primary px-2 font-semibold text-white">
                  {t('quote_accepted', { ns: 'hub' })}
                </span>
              </div>
            )}
            <JobListingType job={context.job} t={t} />
          </>
        ) : (
          <div className="flex items-center gap-2">
            {/* Badge de Rôle (Principal vs Alliance) */}
            {context.view_type === 'freelancer' ? (
              <div className="rounded-full bg-primary px-3 py-2 text-sm font-semibold text-white">
                {t('role.principal', { ns: 'hub', defaultValue: 'Pigiste principal' })}
              </div>
            ) : (
              <div className="rounded-full border border-primary px-3 py-2 text-sm font-semibold text-primary">
                {t('role.alliance', { ns: 'hub', defaultValue: 'Alliance' })}
              </div>
            )}

            {hubQuote && (
              <div className="whitespace-nowrap rounded-full bg-gray-100 px-3 py-2 text-center text-sm font-medium text-secondary">
                {t(`status.${hubQuote.status}`, { ns: 'quotes', defaultValue: hubQuote.status })}
              </div>
            )}
          </div>
        )}

        {context.job.status === 'in_progress' && (
          <DropdownSelect<ProjectStatusOption>
            value={selectedStatus}
            onChange={(_, item) => item && setSelectedStatus(item)}
            options={statusOptions}
            getOptionLabel={status => status.label}
            getOptionValue={status => status.id}
          />
        )}
      </div>

      {/* Mobile Details Modal */}
      {showMobileDetails && (
        <div className="mb-4 rounded-lg border bg-white p-6 md:hidden">
          <div className="animate-fade-in absolute right-0 top-16 z-40 flex w-full flex-col gap-2 rounded-bl-3xl rounded-br-3xl bg-white p-4 shadow-2xl">
            <JobDetailsPanel job={context.job} />
          </div>
        </div>
      )}

      {shouldShowCommunicationHub &&
        (isRecruiterView ? (
          <RecruiterHubView
            context={context}
            selectedQuoteId={selectedQuoteId}
            onSelectedQuoteIdChange={setSelectedQuoteId}
          />
        ) : (isFreelancerView || isAllianceView) && principalQuoteIdToFetch ? (
          hubQuote != null ? (
            <FreelancerHubView
              context={context}
              quote={hubQuote}
              quoteId={principalQuoteIdToFetch}
              selectedQuoteId={selectedQuoteId}
              onSelectedQuoteIdChange={setSelectedQuoteId}
            />
          ) : hubQuoteError ? (
            <p className="text-red-600">{t('quote_load_error')}</p>
          ) : (
            <div className="flex min-h-32 items-center justify-center">
              {hubQuoteLoading ? (
                <LoadingSpinner />
              ) : (
                <p className="text-gray-500">{t('loading')}</p>
              )}
            </div>
          )
        ) : null)}

      {quoteForEdit && jobId && (
        <UpsertQuoteModal
          quote={quoteForEdit}
          job={quoteForEdit.job}
          onClose={() => {
            setQuoteForEdit(null);
            fetchHubContext(jobId);
            if (principalQuoteIdToFetch) fetchQuoteById(principalQuoteIdToFetch);
          }}
        />
      )}
    </div>
  );
}
